// src/modules/debug.js — Logger, PHI redaction, network logging, upload diagnostics
//
// Pure utilities that don't read/write app state variables directly.
// Functions that need app state (sendDiagnosticReport, collectFeedbackDiagnostics,
// apiFetchWithRefresh) remain in main.js and import these helpers.
import {
    APP_VERSION, API_BASE,
    MAX_NETWORK_LOGS, MAX_UPLOAD_PROGRESS_LOGS, MAX_UPLOAD_DIAGNOSTICS,
} from '../utils/constants.js';
import { escapeHtml, showView } from '../utils/dom.js';
import { apiFetch, clearSessionToken } from '../utils/api.js';
import { state } from '../state.js';

// ============================================================================
// NETWORK LOGGING — Captures all API request/response pairs for diagnostics
// ============================================================================
const recentNetworkLogs = []; // Keep last 20 API call records

export function addNetworkLog(entry) {
    recentNetworkLogs.push(entry);
    if (recentNetworkLogs.length > MAX_NETWORK_LOGS) recentNetworkLogs.shift();
}

export function getRecentNetworkLogs() {
    return recentNetworkLogs;
}

// ============================================================================
// PHI REDACTION
// ============================================================================
const PHI_LOG_KEYS = [
    'patientLabel', 'soapNote', 'transcript', 'transcriptText', 'text',
    'note', 'displayName', 'email',
    's3Key', 'audioKey', 'noteKey', 'transcriptKey', 'mediaKey', 'objectKey',
    'key', 'Key',
];
const SECRET_LOG_KEYS = [
    'token', 'sessionToken', 'idToken', 'Authorization', 'authorization',
    'credential', 'pin', 'accessToken', 'refreshToken',
];

const S3_KEY_VALUE_PATTERNS = [
    /(?:s3:\/\/[^\s"'\\]+?\/)?scribe\/[^"'\n\r\\]*?\.(?:m4a|wav|webm|mp4|mp3|ogg|flac|amr|json|png|txt)/gi,
    /(?:s3:\/\/[^\s"'\\]+?\/)?scribe\/[A-Za-z0-9._\-/]*/gi,
    /\d{8}-\d{6}-[^"'\n\r\\]*?\.(?:m4a|wav|webm|mp4)/gi,
];

/** FIX N7: strip anything shaped like an audio/note/transcript S3 key. */
export function redactKeyShapedValues(str) {
    if (typeof str !== 'string' || !str) return str;
    let out = str;
    for (const pattern of S3_KEY_VALUE_PATTERNS) {
        out = out.replace(pattern, '[redacted-key]');
    }
    return out;
}

/**
 * FIX N1: Redact a JSON-ish string so no PHI or credential VALUE survives.
 */
export function redactForLogs(raw, maxLen = 200) {
    if (raw === null || raw === undefined) return '';
    let str;
    try { str = typeof raw === 'string' ? raw : JSON.stringify(raw); } catch (e) { return '[unserializable]'; }
    if (typeof str !== 'string') return '';
    for (const key of PHI_LOG_KEYS.concat(SECRET_LOG_KEYS)) {
        str = str.replace(new RegExp('("' + key + '"\\s*:\\s*)"(?:[^"\\\\]|\\\\.)*"', 'g'), '$1"[redacted]"');
    }
    str = str.replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/g, 'Bearer [redacted]');
    str = redactKeyShapedValues(str);
    return str.substring(0, maxLen);
}

/**
 * FIX N1: scrubber applied to EVERY log payload that leaves the device.
 */
export function sanitizeLogs(logs) {
    return (logs || []).map(entry => {
        const clean = { ...entry };
        if (clean.data !== null && clean.data !== undefined) {
            try {
                clean.data = JSON.parse(redactForLogs(JSON.stringify(clean.data), 1000000));
            } catch (e) {
                clean.data = '[redacted: unserializable]';
            }
        }
        if (clean.message) {
            clean.message = String(clean.message).replace(/patient\s*[:=]\s*\S+/gi, 'patient: [patient]');
            clean.message = redactKeyShapedValues(clean.message);
        }
        return clean;
    });
}

// ============================================================================
// UPLOAD PROGRESS & DIAGNOSTICS LOGGING
// ============================================================================
const recentUploadProgressLogs = [];

export function addUploadProgressLog(entry) {
    recentUploadProgressLogs.push(entry);
    if (recentUploadProgressLogs.length > MAX_UPLOAD_PROGRESS_LOGS) recentUploadProgressLogs.shift();
}

export function getRecentUploadProgressLogs() {
    return recentUploadProgressLogs;
}

const recentUploadDiagnostics = [];

export function addUploadDiagnostic(record) {
    recentUploadDiagnostics.push(record);
    if (recentUploadDiagnostics.length > MAX_UPLOAD_DIAGNOSTICS) recentUploadDiagnostics.shift();
}

export function getRecentUploadDiagnostics() {
    return recentUploadDiagnostics;
}

/**
 * PRE-UPLOAD DIAGNOSTIC — Run before every upload attempt.
 */
export async function preUploadDiagnostic(blob, source, loggerRef) {
    const diagStart = Date.now();
    const diag = {
        timestamp: new Date().toISOString(),
        phase: 'pre-upload',
        source: source,
        blobSize: blob.size,
        blobType: blob.type || '',
        blobConstructor: blob.constructor.name,
        isFile: blob instanceof File,
        fileName: (blob instanceof File) ? blob.name : null,
        isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
        isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
        platform: navigator.platform || navigator.userAgentData?.platform || 'unknown',
        visibilityState: document.visibilityState,
        online: navigator.onLine,
        connectionType: navigator.connection ? navigator.connection.effectiveType : 'unknown',
        downlink: navigator.connection ? navigator.connection.downlink : null,
        rtt: navigator.connection ? navigator.connection.rtt : null,
        blobReadable: null,
        blobReadError: null,
        blobReadTimeMs: null,
        blobFirstBytes: null,
        blobLastBytes: null,
        apiReachable: null,
        apiPingMs: null,
        apiPingError: null,
    };

    // BLOB READABILITY TEST
    try {
        const readStart = Date.now();
        const firstSlice = blob.slice(0, 1024);
        const firstBuffer = await firstSlice.arrayBuffer();
        diag.blobFirstBytes = firstBuffer.byteLength;
        if (blob.size > 1024) {
            const lastSlice = blob.slice(Math.max(0, blob.size - 1024), blob.size);
            const lastBuffer = await lastSlice.arrayBuffer();
            diag.blobLastBytes = lastBuffer.byteLength;
        } else {
            diag.blobLastBytes = diag.blobFirstBytes;
        }
        diag.blobReadable = true;
        diag.blobReadTimeMs = Date.now() - readStart;
    } catch (readErr) {
        diag.blobReadable = false;
        diag.blobReadError = readErr.name + ': ' + readErr.message;
        diag.blobReadTimeMs = Date.now() - diagStart;
    }

    // Quick API connectivity test
    try {
        const pingStart = Date.now();
        const pingRes = await fetch(API_BASE + '/health', {
            method: 'HEAD',
            signal: AbortSignal.timeout(5000),
        }).catch(e => ({ ok: false, _error: e }));
        if (pingRes._error) {
            diag.apiReachable = false;
            diag.apiPingError = pingRes._error.name + ': ' + pingRes._error.message;
        } else {
            diag.apiReachable = pingRes.ok || pingRes.status < 500;
            diag.apiPingMs = Date.now() - pingStart;
        }
    } catch (pingErr) {
        diag.apiReachable = false;
        diag.apiPingError = pingErr.name + ': ' + pingErr.message;
    }

    diag.totalDiagTimeMs = Date.now() - diagStart;
    loggerRef.info('UPLOAD', 'Pre-upload diagnostic complete', {
        source: diag.source, blobSize: diag.blobSize, blobConstructor: diag.blobConstructor,
        blobReadable: diag.blobReadable, blobReadError: diag.blobReadError,
        isSafari: diag.isSafari, online: diag.online, apiReachable: diag.apiReachable,
    });
    return diag;
}

/**
 * UPLOAD RESULT TELEMETRY — Called after each upload attempt.
 */
export function createUploadResultTelemetry(options) {
    return {
        timestamp: new Date().toISOString(),
        phase: 'post-upload',
        success: options.success || false,
        method: options.method || 'unknown',
        durationMs: options.durationMs || 0,
        bytesPerSecond: options.success && options.durationMs > 0 && options.blobSize
            ? Math.round(options.blobSize / (options.durationMs / 1000))
            : null,
        blobSize: options.blobSize || 0,
        errorName: options.errorName || null,
        errorMessage: options.errorMessage || null,
        errorType: options.errorType || null,
        httpStatus: options.httpStatus || null,
        lastProgressBytes: options.lastProgressBytes || null,
        progressEventCount: options.progressEventCount || null,
        usedFallback: options.usedFallback || false,
        fallbackReason: options.fallbackReason || null,
        s3ResponseHeaders: options.s3ResponseHeaders || null,
        s3ResponseStatus: options.s3ResponseStatus || null,
        s3ResponseStatusText: options.s3ResponseStatusText || null,
    };
}

// ============================================================================
// STRUCTURED LOGGER MODULE
// ============================================================================
const DentScribeLogger = (() => {
    const MAX_ENTRIES = 500;
    const logs = [];
    const listeners = [];

    function getDeviceInfo() {
        const info = {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            online: navigator.onLine,
            language: navigator.language,
        };
        if (navigator.connection) {
            info.connection = {
                effectiveType: navigator.connection.effectiveType,
                downlink: navigator.connection.downlink,
                rtt: navigator.connection.rtt,
                saveData: navigator.connection.saveData,
            };
        }
        info.mediaRecorderSupport = {
            webmOpus: MediaRecorder.isTypeSupported('audio/webm;codecs=opus'),
            mp4: MediaRecorder.isTypeSupported('audio/mp4'),
            webm: MediaRecorder.isTypeSupported('audio/webm'),
        };
        return info;
    }

    function addEntry(level, category, message, data, error) {
        const entry = {
            timestamp: new Date().toISOString(),
            level, category, message,
        };
        if (data !== undefined && data !== null) entry.data = data;
        if (error) {
            entry.error = { name: error.name, message: error.message, stack: error.stack };
        }
        if (level === 'ERROR') entry.deviceInfo = getDeviceInfo();
        logs.push(entry);
        if (logs.length > MAX_ENTRIES) logs.shift();
        listeners.forEach(fn => { try { fn(entry); } catch(e) {} });
        return entry;
    }

    function info(category, message, data) {
        const entry = addEntry('INFO', category, message, data);
        console.log(`[${category}] ${message}`, data || '');
        return entry;
    }

    function warn(category, message, data) {
        const entry = addEntry('WARN', category, message, data);
        console.warn(`[${category}] ${message}`, data || '');
        return entry;
    }

    function error(category, message, err, data) {
        const entry = addEntry('ERROR', category, message, data, err);
        console.error(`[${category}] ${message}`, err, data || '');
        return entry;
    }

    function getLogs(filter) {
        if (!filter || filter === 'ALL') return [...logs];
        if (filter === 'ERRORS') return logs.filter(l => l.level === 'ERROR');
        return logs.filter(l => l.category === filter);
    }

    function clear() {
        logs.length = 0;
        listeners.forEach(fn => { try { fn(null); } catch(e) {} });
    }

    function exportJSON() {
        return JSON.stringify({
            exportedAt: new Date().toISOString(),
            appVersion: APP_VERSION,
            deviceInfo: getDeviceInfo(),
            entries: logs,
        }, null, 2);
    }

    function onNewEntry(fn) { listeners.push(fn); }
    function removeListener(fn) {
        const i = listeners.indexOf(fn);
        if (i >= 0) listeners.splice(i, 1);
    }

    return { info, warn, error, getLogs, clear, exportJSON, onNewEntry, removeListener, getDeviceInfo };
})();

export const logger = DentScribeLogger;

// ============================================================================
// DEVICE CAPABILITY TELEMETRY — WebGPU / on-device-Whisper feasibility census
// ============================================================================
// Derive a COARSE, non-identifying browser/OS bucket from the user-agent.
// The raw userAgent is NEVER sent to /telemetry/device (it is treated as
// potentially identifying); only these buckets are transmitted.
function parseUserAgentCoarse(ua) {
    ua = ua || '';
    let browserFamily = 'Other';
    let browserMajor = 0;
    let osFamily = 'Other';

    // OS family (order matters: iOS/Android before macOS/Windows/Linux)
    if (/iPhone|iPad|iPod/.test(ua)) osFamily = 'iOS';
    else if (/Android/.test(ua)) osFamily = 'Android';
    else if (/Mac OS X|Macintosh/.test(ua)) osFamily = 'macOS';
    else if (/Windows/.test(ua)) osFamily = 'Windows';
    else if (/Linux|X11/.test(ua)) osFamily = 'Linux';

    // Browser family (order matters: Edge/Chrome before Safari; Safari has no "Chrome")
    let m;
    if ((m = ua.match(/Edg\/(\d+)/))) { browserFamily = 'Edge'; browserMajor = +m[1]; }
    else if ((m = ua.match(/Chrome\/(\d+)/)) && !/Edg\//.test(ua)) { browserFamily = 'Chrome'; browserMajor = +m[1]; }
    else if ((m = ua.match(/Firefox\/(\d+)/))) { browserFamily = 'Firefox'; browserMajor = +m[1]; }
    else if (/Safari\//.test(ua) && (m = ua.match(/Version\/(\d+)/))) { browserFamily = 'Safari'; browserMajor = +m[1]; }

    return { browserFamily, browserMajor, osFamily };
}

// Build the capability payload. Async because navigator.gpu.requestAdapter() is
// the REAL signal: a browser may expose navigator.gpu yet have no usable adapter.
// Every field is non-PHI; raw userAgent/platform/screen are intentionally omitted.
export async function getDeviceCapabilities() {
    const hasWebGPU = !!(navigator.gpu);
    let webgpuAdapter = false;
    if (hasWebGPU) {
        try {
            const adapter = await navigator.gpu.requestAdapter();
            webgpuAdapter = !!adapter;
        } catch (e) {
            webgpuAdapter = false;
        }
    }
    const coarse = parseUserAgentCoarse(navigator.userAgent);
    return {
        hasWebGPU,
        webgpuAdapter,
        hardwareConcurrency: navigator.hardwareConcurrency || 0,
        deviceMemory: navigator.deviceMemory || 0,
        browserFamily: coarse.browserFamily,
        browserMajor: coarse.browserMajor,
        osFamily: coarse.osFamily,
    };
}

// ============================================================================
// DEBUG PANEL — init, render, copy, download
// ============================================================================
export function initDebugPanel(openFeedbackModalFn) {
    const panel = document.getElementById('debugPanel');
    const toggle = document.getElementById('debugToggleBtn');
    if (!panel || !toggle) return;

    let longPressTimer = null;
    let longPressTriggered = false;

    toggle.addEventListener('mousedown', () => {
        longPressTriggered = false;
        longPressTimer = setTimeout(() => {
            longPressTriggered = true;
            panel.classList.toggle('open');
            if (panel.classList.contains('open')) renderDebugLogs();
        }, 3000);
    });
    toggle.addEventListener('mouseup', () => {
        clearTimeout(longPressTimer);
        if (!longPressTriggered) openFeedbackModalFn();
    });
    toggle.addEventListener('mouseleave', () => { clearTimeout(longPressTimer); });
    toggle.addEventListener('touchstart', () => {
        longPressTriggered = false;
        longPressTimer = setTimeout(() => {
            longPressTriggered = true;
            panel.classList.toggle('open');
            if (panel.classList.contains('open')) renderDebugLogs();
        }, 3000);
    });
    toggle.addEventListener('touchend', (e) => {
        clearTimeout(longPressTimer);
        if (!longPressTriggered) { e.preventDefault(); openFeedbackModalFn(); }
    });
    toggle.addEventListener('touchcancel', () => { clearTimeout(longPressTimer); });

    document.querySelectorAll('.debug-filter-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('.debug-filter-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            renderDebugLogs(btn.dataset.filter);
        });
    });

    document.getElementById('debugCopyBtn').addEventListener('click', copyDebugLogs);
    document.getElementById('debugDownloadBtn').addEventListener('click', downloadDebugLogs);
    document.getElementById('debugClearBtn').addEventListener('click', () => {
        logger.clear(); renderDebugLogs();
    });

    // Live update
    logger.onNewEntry(() => {
        if (panel.classList.contains('open')) {
            const activeFilter = document.querySelector('.debug-filter-btn.active');
            renderDebugLogs(activeFilter ? activeFilter.dataset.filter : 'ALL');
        }
    });

    logger.info('SYSTEM', 'App initialized', { version: APP_VERSION, deviceInfo: logger.getDeviceInfo() });
}

export function renderDebugLogs(filter) {
    const container = document.getElementById('debugLogList');
    if (!container) return;
    const logs = logger.getLogs(filter || 'ALL');
    const recentLogs = logs.slice(-100);
    if (recentLogs.length === 0) {
        container.innerHTML = '<div class="debug-empty">No logs yet</div>';
        return;
    }
    container.innerHTML = recentLogs.map(entry => {
        const time = entry.timestamp.substring(11, 23);
        const levelClass = 'debug-level-' + entry.level.toLowerCase();
        const dataStr = entry.data ? ' ' + JSON.stringify(entry.data).substring(0, 120) : '';
        const errorStr = entry.error ? ' [' + entry.error.name + ': ' + entry.error.message + ']' : '';
        return `<div class="debug-entry ${levelClass}">
            <span class="debug-time">${time}</span>
            <span class="debug-cat">${entry.category}</span>
            <span class="debug-msg">${escapeHtml(entry.message)}${escapeHtml(errorStr)}${escapeHtml(dataStr)}</span>
        </div>`;
    }).join('');
    container.scrollTop = container.scrollHeight;
}

export async function copyDebugLogs() {
    const json = logger.exportJSON();
    try {
        await navigator.clipboard.writeText(json);
        const btn = document.getElementById('debugCopyBtn');
        btn.textContent = '✓ Copied';
        setTimeout(() => { btn.textContent = '📋 Copy'; }, 2000);
    } catch (e) {
        const ta = document.createElement('textarea');
        ta.value = json;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
    }
}

export function downloadDebugLogs() {
    const json = logger.exportJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dentscribe-logs-' + new Date().toISOString().replace(/[:.]/g, '-') + '.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// ============================================================================
// apiFetchWithRefresh — authenticated fetch with network logging & 401 handling
// ============================================================================
export async function apiFetchWithRefresh(path, options = {}) {
    const reqStart = Date.now();
    const method = options.method || 'GET';
    const requestBodyLength = options.body ? String(options.body).length : 0;

    const res = await apiFetch(path, options);
    const durationMs = Date.now() - reqStart;

    let responseBodyLength = null;
    let errorPreview = null;
    let errorCode = null;
    try {
        const cloned = res.clone();
        const text = await cloned.text();
        responseBodyLength = text.length;
        if (!res.ok) {
            errorPreview = redactForLogs(text, 200);
            try {
                const parsed = JSON.parse(text);
                errorCode = parsed.errorCode || parsed.code || (typeof parsed.error === 'string' ? redactForLogs(parsed.error, 80) : null);
            } catch (e) { /* not JSON */ }
        }
    } catch (e) { /* body already consumed or unreadable */ }

    addNetworkLog({
        timestamp: new Date().toISOString(),
        method,
        path,
        status: res.status,
        ok: res.ok,
        durationMs,
        requestBodyLength,
        responseBodyLength,
        errorCode: errorCode,
        errorPreview: errorPreview,
    });

    if (res.status === 401) {
        logger.warn('AUTH', 'Got 401 on ' + path + ' — session expired, redirecting to sign-in');
        clearSessionToken();
        state.currentUser = null;
        state.isAdmin = false;
        showView('view-signin');
        return res;
    }

    if (res.status === 402) {
        const body = await res.clone().json().catch(() => ({}));
        if (body.error === 'trial_expired') {
            // MUST NOT clear session/state/localStorage — unsent notes and the session are preserved.
            logger.warn('AUTH', 'trial_expired on ' + path + ' — routing to blocked view');
            showView('view-blocked');
        }
        return res;
    }

    return res;
}
